import { motion } from "framer-motion";
import { ArrowDown } from "lucide-react";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: [0.33, 1, 0.68, 1] },
};

export const HeroSection = () => {
  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col overflow-hidden"
    >
      {/* Background image */}
      <div className="absolute inset-0 z-0">
        <img
          src="/images/hero.png"
          alt="INSTINCT Krav Maga Training"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-page via-page/85 to-page/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-page via-transparent to-page/50" />
      </div>

      {/* Top nav brand mark */}
      <motion.nav
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3, duration: 0.6 }}
        className="relative z-20 flex items-center justify-between px-6 md:px-16 lg:px-24 pt-8"
      >
        <span
          data-testid="brand-mark"
          className="font-['Oswald'] text-base md:text-lg font-bold uppercase tracking-[0.3em] text-ink"
        >
          INSTINCT
        </span>
        <button
          onClick={() => scrollTo("contact")}
          className="text-[10px] md:text-xs tracking-[0.2em] uppercase text-sig font-['IBM_Plex_Sans'] font-bold hover:text-ink transition-colors border border-sig/40 px-5 py-2 hover:bg-sig/10"
        >
          Get in Touch
        </button>
      </motion.nav>

      {/* Bleed text */}
      <div className="hero-bleed" aria-hidden="true">
        FIGHT
      </div>

      {/* Main content */}
      <div className="relative z-10 flex-1 flex flex-col justify-end px-6 md:px-16 lg:px-24 pb-20 md:pb-24 max-w-5xl">
        <motion.div {...fadeUp} className="brass-line mb-6" />

        <motion.p
          {...fadeUp}
          data-testid="hero-pre-headline"
          className="text-xs md:text-sm tracking-[0.3em] uppercase text-sig font-['IBM_Plex_Sans'] font-bold mb-4"
        >
          India's Leading Dedicated Centre For
        </motion.p>

        <motion.h1
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.1 }}
          data-testid="hero-headline"
          className="font-['Oswald'] text-[16vw] md:text-[12vw] lg:text-[9vw] font-bold uppercase tracking-[-0.03em] leading-[0.85] text-ink"
        >
          KRAV<br />MAGA
        </motion.h1>

        <motion.p
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.2 }}
          data-testid="hero-tagline"
          className="mt-6 text-base md:text-lg leading-relaxed text-mute font-['IBM_Plex_Sans'] font-light max-w-lg"
        >
          The world's most effective self-defense system. Born in combat. Built for real life.
        </motion.p>

        <motion.div
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.25 }}
          data-testid="hero-mission"
          className="mt-8 border-l-2 border-sig pl-5 md:pl-6 max-w-xl"
        >
          <span className="text-[9px] tracking-[0.3em] uppercase text-sig font-['IBM_Plex_Sans'] font-bold block mb-2">
            Our Mission
          </span>
          <p className="text-sm md:text-base leading-relaxed text-ink font-['IBM_Plex_Sans'] font-light">
            We started Instinct because too many people move through the world afraid. Our mission is to replace that fear with power — so that nobody gets to make you a victim.
          </p>
        </motion.div>

        <motion.div
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.3 }}
          className="mt-10 flex flex-col sm:flex-row gap-4"
        >
          <button
            data-testid="book-session-btn"
            onClick={() => scrollTo("contact")}
            className="bg-sig text-sigfg px-10 py-4 text-xs font-bold uppercase tracking-[0.25em] font-['IBM_Plex_Sans'] hover:bg-sig2 transition-colors duration-300"
          >
            Start Training
          </button>
          <button
            data-testid="learn-more-btn"
            onClick={() => scrollTo("krav-maga")}
            className="border border-ink/20 bg-transparent text-ink px-10 py-4 text-xs font-bold uppercase tracking-[0.25em] font-['IBM_Plex_Sans'] hover:bg-ink hover:text-page transition-all duration-300 flex items-center justify-center gap-3"
          >
            What is Krav Maga?
            <ArrowDown className="w-3 h-3" />
          </button>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 0.8 }}
        className="absolute bottom-8 right-6 md:right-16 lg:right-24 z-10"
      >
        <div className="flex items-center gap-4">
          <span className="text-[10px] tracking-[0.3em] uppercase text-sig/50 font-['IBM_Plex_Sans']">
            Scroll
          </span>
          <div className="w-[1px] h-12 bg-sig/30" />
        </div>
      </motion.div>
    </section>
  );
};
